/**
 * SOLID Review
 * S — Strong. Sole job: interpret an encoded Meyer score (which integers are legal claims, how to label
 *     one for display, how to decode it back to the two die values that produced it).
 * O — Good. A new score class (e.g. a rule variant) is a new branch in each function, following the same
 *     score-range pattern already established.
 * L — N/A — no inheritance hierarchy; plain functions and a const array.
 * I — Strong. Three narrow, single-purpose exports (VALID_SCORES, scoreLabel, scoreToDice), each usable
 *     independently.
 * D — Strong. Zero imports — pure functions over primitives, no dependency on any other layer.
 */
// Pure domain logic for interpreting an encoded Meyer score: which integers are
// legal claims, how to label one for display, and how to decode it back to the
// two die values that produced it. Extracted from ControlPanel.tsx (Part 6),
// where this exact logic lived inline and duplicated a since-deleted, unused
// domain/dice/DiceRankingService.ts from an earlier fabricated-scaffolding
// commit (see plan/plan_frontend_makeover.md Part 2) -- this file is a fresh
// extraction of the real, production-used logic, not a revival of that one.

// All valid Meyer scores in ascending rank order (Danish rules).
// Regular: 32..65, Doubles (pairs): 110=11s..160=66s, Little Meyer (13)=900, Meyer=1000.
// Note: 3-1 is not score 31 -- it is "Little Meyer" at rank 900.
export const VALID_SCORES = [32, 41, 42, 43, 51, 52, 53, 54, 61, 62, 63, 64, 65, 110, 120, 130, 140, 150, 160, 900, 1000] as const;

/** Meyer, the top rank: it cannot be believed, only lifted (frontend strict-ports plan, FE1 — named once). */
export const MEYER_RANK = 1000;

export function scoreLabel(score: number): string {
  if (score === 1000) return 'Meyer (21)';
  if (score === 900) return 'Little Meyer (31)';
  if (score >= 110 && score <= 160) { const d = (score - 100) / 10; return `Pair ${d}s`; }
  const h = Math.floor(score / 10), l = score % 10;
  return `${h}-${l}`;
}

/** Decode a Meyer score integer back to the two die values [high, low]. */
export function scoreToDice(score: number): [number, number] {
  if (score === 1000) return [2, 1];
  if (score === 900) return [3, 1]; // Little Meyer
  if (score >= 110 && score <= 160) { const d = (score - 100) / 10; return [d, d]; }
  return [Math.floor(score / 10), score % 10];
}
